/**
 * @fileoverview Redux-based data utilities to replace SimpleDB API calls.
 * Provides functions that mimic the original API behavior using Redux state management.
 */

import {
    saveDonationInfoData,
    saveFormGuidData,
    setFormInstanceData,
    setFormPublishStatus,
    setStreamInfoData
} from '../../../actions';

/**
 * Create form instance data in Redux (replaces /createforminstance API)
 * @param {Function} dispatch - Redux dispatch function
 * @param {Object} instanceData - Form instance data to store
 * @returns {Promise<Object>} Success response
 */
// eslint-disable-next-line require-await -- async by contract: callers rely on the returned promise
export const createFormInstance = async (dispatch, instanceData) => {
    try {
        dispatch(setFormInstanceData(instanceData));

        return {
            success: true,
            data: {
                body: {
                    message: 'Form instance created successfully',
                    instanceData
                }
            }
        };
    } catch {
        throw new Error('Failed to create form instance');
    }
};

/**
 * Save form GUID data in Redux (replaces /saveformguid API)
 * @param {Function} dispatch - Redux dispatch function
 * @param {Object} formGuidData - Form GUID data to save
 * @returns {Promise<Object>} Success response
 */
// eslint-disable-next-line require-await -- async by contract: callers rely on the returned promise
export const saveFormGuid = async (dispatch, formGuidData) => {
    try {
        const dataToSave = {
            eId: formGuidData.eId,
            guid: formGuidData.guid,
            giventId: formGuidData.giventId,
            timestamp: new Date().toISOString(),
            ...formGuidData
        };

        dispatch(saveFormGuidData(dataToSave));

        return {
            success: true,
            data: {
                body: {
                    message: 'Form GUID saved successfully',
                    savedData: dataToSave
                }
            }
        };
    } catch {
        throw new Error('Failed to save form GUID');
    }
};

/**
 * Get user form data from Redux (replaces /getuserform API)
 * @param {Object} state - Redux state
 * @param {string} meetingUUID - Meeting UUID to search for
 * @returns {Promise<Object>} Form data response
 */
// eslint-disable-next-line require-await -- async by contract: callers rely on the returned promise
export const getUserForm = async (state, meetingUUID) => {
    try {
        const formGuidData = state['features/clickandpledge']?.formGuidData || {};

        // Check if form exists for this meeting
        if (formGuidData.eId === meetingUUID && formGuidData.guid) {
            // Simulate the original API response structure
            const mockResponse = {
                data: {
                    body: {
                        Items: [ {
                            Attributes: [
                                {
                                    Name: 'eId',
                                    Value: formGuidData.eId
                                },
                                {
                                    Name: 'timestamp',
                                    Value: formGuidData.timestamp
                                },
                                {
                                    Name: 'giventId',
                                    Value: formGuidData.giventId
                                },
                                {
                                    Name: 'guid',
                                    Value: formGuidData.guid
                                },
                                {
                                    Name: 'published',
                                    Value: state['features/clickandpledge']?.formPublishStatus || false
                                },
                                {
                                    Name: 'giventId',
                                    Value: formGuidData.giventId
                                }
                            ]
                        } ]
                    }
                }
            };

            return mockResponse;
        }

        // Return empty response if no form found
        return {
            data: {
                body: {
                    Items: []
                }
            }
        };
    } catch {
        throw new Error('Failed to get user form');
    }
};

/**
 * Publish form in Redux (replaces /publishform API)
 * @param {Function} dispatch - Redux dispatch function
 * @param {Object} publishData - Form publish data
 * @returns {Promise<Object>} Success response
 */
// eslint-disable-next-line require-await -- async by contract: callers rely on the returned promise
export const publishForm = async (dispatch, publishData) => {
    try {
        dispatch(setFormPublishStatus(true));

        return {
            success: true,
            data: {
                body: {
                    message: 'Form published successfully',
                    publishData,
                    published: true
                }
            }
        };
    } catch {
        throw new Error('Failed to publish form');
    }
};

/**
 * Save donation info in Redux (replaces /saveinfo API)
 * @param {Function} dispatch - Redux dispatch function
 * @param {Object} donationInfo - Donation information to save
 * @returns {Promise<Object>} Success response
 */
// eslint-disable-next-line require-await -- async by contract: callers rely on the returned promise
export const saveDonationInfo = async (dispatch, donationInfo) => {
    try {
        const donationData = {
            ...donationInfo,
            timestamp: new Date().toISOString(),
            id: `donation_${Date.now()}_${Math.random().toString(36)
.substr(2, 9)}`
        };

        // Get current donation info array and add new donation
        const currentDonations = donationInfo.currentDonations || [];
        const updatedDonations = [ ...currentDonations, donationData ];

        dispatch(saveDonationInfoData(updatedDonations));

        return {
            success: true,
            data: {
                body: {
                    message: 'Donation info saved successfully',
                    donationData
                }
            }
        };
    } catch {
        throw new Error('Failed to save donation info');
    }
};

/**
 * Get stream info from Redux (replaces /getstreaminfo API)
 * @param {Object} state - Redux state
 * @param {string} meetingUUID - Meeting UUID to get stream info for
 * @returns {Promise<Object>} Stream info response
 */
// eslint-disable-next-line require-await -- async by contract: callers rely on the returned promise
export const getStreamInfo = async (state, meetingUUID) => {
    try {
        const donationInfoData = state['features/clickandpledge']?.donationInfoData || [];
        const streamInfoData = state['features/clickandpledge']?.streamInfoData || [];

        // Filter donations for this meeting
        const meetingDonations = donationInfoData.filter(donation =>
            donation.meetingUUID === meetingUUID || donation.eId === meetingUUID
        );

        // Simulate the original API response structure for marquee/stream
        const streamResponse = {
            data: {
                body: {
                    donations: meetingDonations,
                    streamData: streamInfoData,
                    totalAmount: meetingDonations.reduce((sum, donation) =>
                        sum + (parseFloat(donation.amount) || 0), 0
                    ),
                    count: meetingDonations.length
                }
            }
        };

        return streamResponse;
    } catch {
        throw new Error('Failed to get stream info');
    }
};

/**
 * Update stream info in Redux
 * @param {Function} dispatch - Redux dispatch function
 * @param {Object} streamData - Stream data to update
 * @returns {Promise<Object>} Success response
 */
// eslint-disable-next-line require-await -- async by contract: callers rely on the returned promise
export const updateStreamInfo = async (dispatch, streamData) => {
    try {
        dispatch(setStreamInfoData(streamData));

        return {
            success: true,
            data: {
                body: {
                    message: 'Stream info updated successfully',
                    streamData
                }
            }
        };
    } catch {
        throw new Error('Failed to update stream info');
    }
};

/**
 * Check if form is already published (utility function)
 * @param {Object} state - Redux state
 * @param {string} meetingUUID - Meeting UUID to check
 * @returns {boolean} True if form is published
 */
export const checkIfFormAlreadyPublished = (state, meetingUUID) => {
    try {
        const formGuidData = state['features/clickandpledge']?.formGuidData || {};
        const formPublishStatus = state['features/clickandpledge']?.formPublishStatus || false;

        return formGuidData.eId === meetingUUID && formPublishStatus;
    } catch {
        return false;
    }
};

/**
 * Get form data for meeting (utility function)
 * @param {Object} state - Redux state
 * @param {string} meetingUUID - Meeting UUID
 * @returns {Object|null} Form data or null if not found
 */
export const getFormDataForMeeting = (state, meetingUUID) => {
    try {
        const formGuidData = state['features/clickandpledge']?.formGuidData || {};

        if (formGuidData.eId === meetingUUID) {
            return formGuidData;
        }

        return null;
    } catch {
        return null;
    }
};
